import React from 'react';
import { Box, Typography, Button, useTheme, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Footer from '../Sections/Footer';
import { useTranslation } from '../../hooks/useTranslation';

const MoreDetailsContent = ({
	consent,
	onClose,
	showBackButton = false,
	customDetails,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isDark = theme.palette.mode === 'dark';
	const formatDate = (dateString) => {
		if (!dateString) return null;
		const date = new Date(dateString);
		const dataArray = date.toString().split(' ');
		return `${dataArray[2]} ${dataArray[1]} ${dataArray[3]}`;
	};

	const formatDateRange = (fromDate, toDate) => {
		if (!fromDate || !toDate) return '';
		return `${formatDate(fromDate)} → ${formatDate(toDate)}`;
	};

	return (
		<Box
			sx={{
				fontFamily: customDetails?.fontFamily,
				backgroundColor: isDark
					? '#161616'
					: customDetails?.secondaryColor || '#fff',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Box
				sx={{
					flex: 1,
					overflowY: 'auto',
					padding: showBackButton ? '16px 24px' : '16px 24px',
				}}
			>
				{showBackButton ? (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							marginBottom: '20px',
						}}
					>
						<Typography
							variant="h6"
							sx={{
								fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
								fontFamily: customDetails?.fontFamily,
								fontWeight: 600,
								lineHeight: 1,
							}}
						>
							{t('consentRenewal.titles.moreDetails')}
						</Typography>
						<IconButton
							onClick={onClose}
							sx={{
								p: '0.25rem',
								color: isDark ? '#FFFFFF' : '#000000',
								'&:hover': {
									backgroundColor: isDark
										? 'rgba(255, 255, 255, 0.1)'
										: 'rgba(0, 0, 0, 0.04)',
								},
							}}
						>
							<CloseIcon sx={{ fontSize: '1.25rem' }} />
						</IconButton>
					</Box>
				) : (
					<Box sx={{ marginBottom: '20px' }}>
						<Typography
							variant="h6"
							sx={{
								fontSize: '1rem',
								fontFamily: customDetails?.fontFamily,
								fontWeight: 600,
							}}
						>
							{t('consentRenewal.titles.moreDetails')}
						</Typography>
					</Box>
				)}

				<Box
					sx={{
						display: 'flex',
						gap: '40px',
						marginBottom: '16px',
					}}
				>
					<Box sx={{ flex: 1 }}>
						<Typography
							sx={{
								fontSize: '0.75rem',
								fontFamily: customDetails?.fontFamily,
								color: '#666',
								marginBottom: '4px',
							}}
						>
							{t('consentRenewal.labels.consentStartDate')}
						</Typography>
						<Typography
							sx={{
								fontSize: '0.875rem',
								fontFamily: customDetails?.fontFamily,
							}}
						>
							{consent?.startTime ? formatDate(consent?.startTime) : ''}
						</Typography>
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography
							sx={{
								fontSize: '0.75rem',
								fontFamily: customDetails?.fontFamily,
								color: '#666',
								marginBottom: '4px',
							}}
						>
							{t('consentRenewal.labels.consentEndDate')}
						</Typography>
						<Typography
							sx={{
								fontSize: '0.875rem',
								fontFamily: customDetails?.fontFamily,
							}}
						>
							{consent?.expireTime ? formatDate(consent?.expireTime) : ''}
						</Typography>
					</Box>
				</Box>

				<Box sx={{ marginBottom: '16px' }}>
					<Typography
						sx={{
							fontSize: '0.75rem',
							fontFamily: customDetails?.fontFamily,
							color: '#666',
							marginBottom: '4px',
						}}
					>
						{t('consentRenewal.labels.dataWillBeFetchedFor')}
					</Typography>
					<Typography
						sx={{
							fontSize: '0.875rem',
							fontFamily: customDetails?.fontFamily,
						}}
					>
						{formatDateRange(
							consent?.DataDateTimeRange?.from,
							consent?.DataDateTimeRange?.to
						)}
					</Typography>
				</Box>

				<Box sx={{ marginBottom: '16px' }}>
					<Typography
						sx={{
							fontSize: '0.75rem',
							fontFamily: customDetails?.fontFamily,
							color: '#666',
							marginBottom: '4px',
						}}
					>
						{t('consentRenewal.labels.accountDetails')}
					</Typography>
					<Typography
						sx={{
							fontSize: '0.875rem',
							fontFamily: customDetails?.fontFamily,
						}}
					>
						{consent?.consentTypes?.join(', ')}
					</Typography>
				</Box>

				<Typography
					sx={{
						fontSize: '0.75rem',
						fontFamily: customDetails?.fontFamily,
						color: '#666',
						lineHeight: 1.5,
					}}
				>
					{t('consentRenewal.messages.grievanceContact')}{' '}
					grievance@cookiejar.co.in
				</Typography>
			</Box>

			{!showBackButton && (
				<>
					<Box
						sx={{
							padding: '16px 24px',
						}}
					>
						<Button
							variant="outlined"
							fullWidth
							onClick={onClose}
							sx={{
								borderColor: customDetails?.primaryColor || '#4A5EAD',
								color: customDetails?.primaryColor || '#4A5EAD',
								textTransform: 'none',
								fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
								fontFamily: customDetails?.fontFamily,
								padding: '12px 24px',
								borderRadius: '8px',
								backgroundColor: customDetails?.secondaryColor || '#fff',
								'&:hover': {
									borderColor: customDetails?.primaryColor || '#4A5EAD',
									backgroundColor: 'rgba(0,0,0,0.04)',
								},
							}}
						>
							{t('consentRenewal.buttons.understood')}
						</Button>
					</Box>
					<Footer customDetails={customDetails} />
				</>
			)}
		</Box>
	);
};

export default MoreDetailsContent;
